// ====================================
// TIMESHEET ANALYSIS EXPORT FUNCTIONS
// ====================================

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  TimesheetAnalysisConfig,
} from "./types";

import {
  DEFAULT_PDF_STYLES,
} from "./common";
import { TimesheetAnalysisRow } from "./timesheet-analysis-mapper";

// ====================================
// EXCEL EXPORT (NOVO COM EXCELJS)
// ====================================

export const exportTimesheetAnalysisToExcel = async (
  data: TimesheetAnalysisRow[],
  config: TimesheetAnalysisConfig,
): Promise<void> => {

  const workbook = new ExcelJS.Workbook();

  const dailyWorksheet =
    workbook.addWorksheet("Diário");

  const summaryWorksheet =
    workbook.addWorksheet("Resumo");

  const daysInMonth = new Date(
    config.year,
    config.month,
    0,
  ).getDate();

  // ====================================
  // DAILY DATA
  // ====================================

const dailyData = data.map((item) => {
  const row: any = {
    Colaborador: item.formattedName,
  };

  for (let day = 1; day <= daysInMonth; day++) {
    row[day.toString()] =
      item.dailyValues.get(day)?.value ?? "";
  }

  row["H.T."] = item.totalHours;
  row["Férias"] = item.vacationHours;
  row["Ausências"] = item.absenceHours;
  row["H.M."] = item.availableHours;
  row["Total"] = item.totalHoursAll;
  row["Horas Diárias"] = item.workHoursPerDay;

  return row;
});

  // ====================================
  // SUMMARY DATA
  // ====================================

  const summaryData = data.map((item) => ({
  Colaborador: item.name,

  "D.T.": item.daysWorked,

  Férias: item.vacationDays,

  "D.A.": item.absenceDays,

  "D.M.": item.availableDays,

  Total: item.totalDaysAll,

  "P.T.": `${item.percentWorked}%`,

  "P.A.": `${item.percentAbsent}%`,

  Prevenção:
    item.preventionsCount,

  Deslocação:
    item.displacementCount,

  "Horas Extra":
    item.extraHourCount,

  "Horas Diárias":
    item.workHoursPerDay,
}));

  // ====================================
  // DAILY SHEET
  // ====================================

  // ALTERADO:
  // colaborador garantidamente na primeira coluna
  const dailyHeaders = [
    "Colaborador",
    ...Array.from(
      { length: daysInMonth },
      (_, i) => (i + 1).toString(),
    ),
    "H.T.",
    "Férias",
    "Ausências",
    "H.M.",
    "Total",
    "Horas Diárias",
  ];

  dailyWorksheet.columns =
    dailyHeaders.map((key) => ({
      header: key,
      key,
      width:
        key === "Colaborador"
          ? 30
          : 10,
    }));

  dailyData.forEach((row) => {
    dailyWorksheet.addRow(row);
  });

  // ====================================
  // SUMMARY SHEET
  // ====================================

  // ALTERADO:
  // colaborador garantidamente na primeira coluna
  const summaryHeaders = [
    "Colaborador",
    "D.T.",
    "Férias",
    "D.A.",
    "D.M.",
    "Total",
    "P.T.",
    "P.A.",
    "Prevenção",
    "Deslocação",
    "Horas Extra",
    "Horas Diárias",
  ];

  summaryWorksheet.columns =
    summaryHeaders.map((key) => ({
      header: key,
      key,
      width:
        key === "Colaborador"
          ? 30
          : 15,
    }));

  summaryData.forEach((row) => {
    summaryWorksheet.addRow(row);
  });

  // ====================================
  // HEADER STYLE
  // ====================================

  [dailyWorksheet, summaryWorksheet]
    .forEach((sheet) => {

      const headerRow =
        sheet.getRow(1);

      headerRow.font = {
        bold: true,
        color: {
          argb: "FFFFFFFF",
        },
      };

      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "1F4E78",
        },
      };

      headerRow.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
    });

  // ====================================
  // COLORS DAILY CELLS
  // ====================================
   const itemByName = new Map(
  data.map((item) => [
    item.formattedName.trim(),
    item,
  ]),
);

dailyWorksheet.eachRow(
  (row: ExcelJS.Row, rowNumber: number) => {
    if (rowNumber === 1) {
      return;
    }

    const employee =
      itemByName.get(
        String(
          row.getCell(1).value,
        ).trim(),
      );

    if (!employee) {
      return;
    }

    row.eachCell(
      (cell: ExcelJS.Cell, colNumber: number) => {
        if (
          colNumber <= 1 ||
          colNumber >
            daysInMonth + 1
        ) {
          return;
        }

        const dailyCell =
          employee.dailyValues.get(
            colNumber - 1,
          );

        if (!dailyCell) {
          return;
        }

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb:
              dailyCell.color.replace(
                "#",
                "",
              ),
          },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      },
    );
  },
);
  // ====================================
  // FREEZE PANES
  // ====================================

  dailyWorksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
      xSplit: 1,
    },
  ];

  summaryWorksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
      xSplit: 1,
    },
  ];

  // ====================================
  // EXPORT
  // ====================================

  const buffer =
    await workbook.xlsx.writeBuffer();

  const filename =
    config.filename.endsWith(".xlsx")
      ? config.filename
      : `${config.filename}.xlsx`;

  saveAs(
    new Blob([buffer]),
    filename,
  );
};

// ====================================
// PDF EXPORT
// ====================================

/**
 * Exporta análise de timesheet para PDF multi-página (Diário + Resumo)
 */

  // ========== PÁGINA 1: TABELA DIÁRIO ==========
  export const buildDailyPdf = (data:any, config:any) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
   const daysInMonth = new Date(config.year, config.month, 0).getDate();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const { margins, fontSize, colors } = DEFAULT_PDF_STYLES;
  doc.setFontSize(fontSize.title);
  doc.text(`${config.title} - Diário`, pageWidth / 2, 20, { align: 'center' });
  
  if (config.subtitle) {
    doc.setFontSize(fontSize.subtitle);
    doc.text(config.subtitle, pageWidth / 2, 28, { align: 'center' });
  }

  const dailyColumns = [{ header: 'Colaborador', dataKey: 'name' }];
  
  for (let day = 1; day <= daysInMonth; day++) {
    dailyColumns.push({ header: day.toString(), dataKey: `day${day}` });
  }
  
  dailyColumns.push(
    { header: 'H.T.', dataKey: 'hoursWorked' },
    { header: 'Férias', dataKey: 'vacation' },
    { header: 'Ausências', dataKey: 'absences' },
    { header: 'H.M.', dataKey: 'workDays' },
    { header: 'Total', dataKey: 'total' },
    { header: 'Horas Diárias', dataKey: 'workHours' }
  );

 const dailyTableData = data.map(
  (item: TimesheetAnalysisRow) => {
    const row: any = {
      name: item.formattedName,
      raw: item,
    };

    for (
      let day = 1;
      day <= daysInMonth;
      day++
    ) {
      row[`day${day}`] =
        item.dailyValues.get(day)
          ?.value ?? "";
    }

    row.hoursWorked =
      item.totalHours;

    row.vacation =
      item.vacationHours;

    row.absences =
      item.absenceHours;

    row.workDays =
      item.availableHours;

    row.total =
      item.totalHoursAll;

    row.workHours =
      item.workHoursPerDay;

    return row;
  },
);

autoTable(doc, {
  columns: dailyColumns,
  body: dailyTableData,
  startY: config.subtitle ? 35 : 30,
  styles: {
    fontSize: fontSize.small,
    cellPadding: 1,
    halign: "center",
    valign: "middle",
  },
  headStyles: {
    fillColor: colors.header,
    textColor: colors.text,
    fontSize: fontSize.small,
  },
  columnStyles: {
    name: { cellWidth: 25 },
  },
  margin: {
    left: margins.default,
    right: margins.default,
  },
  didParseCell: (hookData) => {
  if (hookData.section !== "body") {
    return;
  }

  const dataKey = String(
    hookData.column.dataKey,
  );

  if (!dataKey.startsWith("day")) {
    return;
  }

  const day = Number(
    dataKey.replace("day", ""),
  );
 const rowData =
  hookData.row.raw as {
    raw: TimesheetAnalysisRow;
  };

const employee =
  rowData.raw;
  const dailyCell =
    employee.dailyValues.get(day);

  if (!dailyCell) {
    return;
  }

  const hex =
    dailyCell.color.replace("#", "");

  hookData.cell.styles.fillColor = [
    parseInt(hex.substring(0, 2), 16),
    parseInt(hex.substring(2, 4), 16),
    parseInt(hex.substring(4, 6), 16),
  ];
},
});

return doc;
};
  
  // ========== PÁGINA 2: TABELA RESUMO ==========
  const buildSummaryPdf = (data: TimesheetAnalysisRow[], config: any) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const daysInMonth = new Date(config.year, config.month, 0).getDate();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const { margins, fontSize, colors } = DEFAULT_PDF_STYLES;
  doc.setFontSize(fontSize.title);
  doc.text(`${config.title} - Resumo`, pageWidth / 2, 20, { align: 'center' });
  
  if (config.subtitle) {
    doc.setFontSize(fontSize.subtitle);
    doc.text(config.subtitle, pageWidth / 2, 28, { align: 'center' });
  }
  
  const summaryColumns = [
    { header: 'Colaborador', dataKey: 'name' },
    { header: 'D.T.', dataKey: 'hoursWorked' },
    { header: 'Férias', dataKey: 'vacation' },
    { header: 'D.A.', dataKey: 'absences' },
    { header: 'D.M.', dataKey: 'workDays' },
    { header: 'Total', dataKey: 'total' },
    { header: 'P.T.', dataKey: 'percentWorked' },
    { header: 'P.A.', dataKey: 'percentAbsent' },
    { header: 'Prevenção', dataKey: 'preventions' },
    { header: 'Deslocação', dataKey: 'displacements' },
    { header: 'Horas Extra', dataKey: 'extraHours' },
    { header: 'Horas Diárias', dataKey: 'workHours' }
  ];
  
  const summaryTableData = data.map(
  (item) => ({
    name:
      item.name,

    hoursWorked:
      item.daysWorked,

    vacation:
      item.vacationDays,

    absences:
      item.absenceDays,

    workDays:
      item.availableDays,

    total:
      item.totalDaysAll,

    percentWorked:
      `${item.percentWorked}%`,

    percentAbsent:
      `${item.percentAbsent}%`,

    preventions:
      item.preventionsCount,

    displacements:
      item.displacementCount,

    extraHours:
      item.extraHourCount,

    workHours:
      item.workHoursPerDay,
  }),
);
  
  autoTable(doc, {
    columns: summaryColumns,
    body: summaryTableData,
    startY: config.subtitle ? 35 : 30,
    styles: {
      fontSize: fontSize.body,
      cellPadding: DEFAULT_PDF_STYLES.cellPadding.default,
      halign: 'center',
      valign: 'middle',
    },
    headStyles: {
      fillColor: colors.header,
      textColor: colors.text,
      fontSize: fontSize.body,
    },
    columnStyles: {
      name: { cellWidth: 40 }
    },
    margin: { left: margins.default, right: margins.default },
    didDrawPage: (data) => {
      if (config.showPageNumbers) {
        doc.setFontSize(fontSize.body);
        doc.text(
          `Página ${data.pageNumber}`,
          pageWidth - margins.default,
          pageHeight - 5,
          { align: 'right' }
        );
      }
    }
  });
 

  return doc;
};
export const exportTimesheetAnalysisAsZip = async (config:TimesheetAnalysisConfig, data:TimesheetAnalysisRow[]) => {
  const zip = new JSZip();

  const dailyDoc = buildDailyPdf(data, config);
  const summaryDoc = buildSummaryPdf(data, config);

  const dailyBlob = dailyDoc.output("blob");
  const summaryBlob = summaryDoc.output("blob");

  zip.file(`${config.filename}_diario.pdf`, dailyBlob);
  zip.file(`${config.filename}_resumo.pdf`, summaryBlob);

  const zipBlob = await zip.generateAsync({ type: "blob" });

  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `${config.filename}.zip`;
  a.click();

  URL.revokeObjectURL(url);
};